import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Typography,
  Tag,
  Button,
  Space,
  Modal,
  Descriptions,
  Avatar,
  Rate,
  Timeline,
  List
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  CarOutlined,
  DollarOutlined,
  StarOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface User {
  id: string;
  name: string;
  phone: string;
  email: string;
  avatar?: string;
  gender: 'male' | 'female' | 'unknown';
  age?: number;
  registerDate: string;
  lastLoginDate: string;
  status: 'active' | 'inactive' | 'banned';
  totalOrders: number;
  totalSpent: number;
  averageRating: number;
  memberLevel: 'bronze' | 'silver' | 'gold' | 'platinum';
  address?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  recentOrders: Array<{
    id: string;
    type: 'rental' | 'ride';
    vehicle: string;
    amount: number;
    date: string;
    status: string;
  }>;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    memberLevel: '',
    keyword: '',
    dateRange: null as any
  });

  // サンプルデータ
  useEffect(() => {
    setUsers([
      {
        id: '1',
        name: '田中太郎',
        phone: '090-1234-5678',
        email: 'tanaka@example.com',
        gender: 'male',
        age: 28,
        registerDate: '2023-01-15',
        lastLoginDate: '2023-12-01',
        status: 'active',
        totalOrders: 25,
        totalSpent: 850000,
        averageRating: 4.8,
        memberLevel: 'gold',
        address: '東京都渋谷区渋谷一丁目1-1',
        emergencyContact: '田中花子',
        emergencyPhone: '090-9999-1234',
        recentOrders: [
          {
            id: 'O20231201001',
            type: 'rental',
            vehicle: 'メルセデスベンツE300',
            amount: 45000,
            date: '2023-12-01',
            status: 'completed'
          },
          {
            id: 'O20231125001',
            type: 'ride',
            vehicle: 'トヨタプリウス',
            amount: 8500,
            date: '2023-11-25',
            status: 'completed'
          }
        ]
      },
      {
        id: '2',
        name: '佐藤花子',
        phone: '080-9876-5432',
        email: 'sato@example.com',
        gender: 'female',
        age: 35,
        registerDate: '2023-03-20',
        lastLoginDate: '2023-11-28',
        status: 'active',
        totalOrders: 12,
        totalSpent: 320000,
        averageRating: 4.6,
        memberLevel: 'silver',
        address: '東京都新宿区新宿三丁目2-2',
        recentOrders: [
          {
            id: 'O20231120001',
            type: 'rental',
            vehicle: 'BMW X5',
            amount: 68000,
            date: '2023-11-20',
            status: 'completed'
          }
        ]
      },
      {
        id: '3',
        name: '山田一郎',
        phone: '070-5555-7777',
        email: 'yamada@example.com',
        gender: 'male',
        age: 42,
        registerDate: '2022-08-10',
        lastLoginDate: '2023-12-01',
        status: 'active',
        totalOrders: 48,
        totalSpent: 1560000,
        averageRating: 4.9,
        memberLevel: 'platinum',
        address: '東京都渋谷区恵比寿一丁目3-3',
        emergencyContact: '山田美子',
        emergencyPhone: '090-7777-8888',
        recentOrders: [
          {
            id: 'O20231130001',
            type: 'rental',
            vehicle: 'メルセデスベンツSクラス',
            amount: 120000,
            date: '2023-11-30',
            status: 'completed'
          }
        ]
      }
    ]);
  }, []);

  const statusColors = {
    active: 'success',
    inactive: 'default',
    banned: 'error'
  };

  const statusLabels = {
    active: '有効',
    inactive: '未アクティベート',
    banned: '利用停止'
  };

  const memberLevelColors = {
    bronze: '#cd7f32',
    silver: '#c0c0c0',
    gold: '#ffd700',
    platinum: '#e5e4e2'
  };

  const memberLevelLabels = {
    bronze: 'ブロンズ会員',
    silver: 'シルバー会員',
    gold: 'ゴールド会員',
    platinum: 'プラチナ会員'
  };

  const genderLabels = {
    male: '男性',
    female: '女性',
    unknown: '不明'
  };

  const columns = [
    {
      title: '会員情報',
      key: 'user',
      width: 200,
      render: (_: any, record: User) => (
        <Space>
          <Avatar src={record.avatar} icon={<UserOutlined />} />
          <div>
            <div><strong>{record.name}</strong></div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.phone}
            </div>
          </div>
        </Space>
      )
    },
    {
      title: '性別/年齢',
      key: 'demographics',
      width: 100,
      render: (_: any, record: User) => (
        <div>
          <div>{genderLabels[record.gender]}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.age ? `${record.age}歳` : '不明'}
          </div>
        </div>
      )
    },
    {
      title: '会員ランク',
      dataIndex: 'memberLevel',
      key: 'memberLevel',
      width: 120,
      render: (level: keyof typeof memberLevelLabels) => (
        <Tag color={memberLevelColors[level]}>{memberLevelLabels[level]}</Tag>
      )
    },
    {
      title: '状況',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: keyof typeof statusLabels) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      )
    },
    {
      title: 'ご利用回数',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      width: 80
    },
    {
      title: '総ご利用金額',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      width: 100,
      render: (amount: number) => `¥${amount.toLocaleString()}`
    },
    {
      title: '評価',
      dataIndex: 'averageRating',
      key: 'averageRating',
      width: 120,
      render: (rating: number) => (
        <Space>
          <Rate disabled defaultValue={rating} style={{ fontSize: 12 }} />
          <span>{rating}</span>
        </Space>
      )
    },
    {
      title: '登録日',
      dataIndex: 'registerDate',
      key: 'registerDate',
      width: 100
    },
    {
      title: '最終ログイン',
      dataIndex: 'lastLoginDate',
      key: 'lastLoginDate',
      width: 100
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: any, record: User) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          詳細
        </Button>
      )
    }
  ];

  const handleViewDetail = (user: User) => {
    setSelectedUser(user);
    setDetailModalVisible(true);
  };

  // フィルタリングロジック
  const filteredUsers = users.filter(user => {
    if (filters.status && user.status !== filters.status) return false;
    if (filters.memberLevel && user.memberLevel !== filters.memberLevel) return false;
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase();
      return (
        user.name.toLowerCase().includes(keyword) ||
        user.phone.includes(keyword) ||
        user.email.toLowerCase().includes(keyword)
      );
    }
    return true;
  });

  const totalStats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    totalOrders: users.reduce((sum, u) => sum + u.totalOrders, 0),
    totalRevenue: users.reduce((sum, u) => sum + u.totalSpent, 0)
  };

  return (
    <div>
      <Title level={2}>会員管理</Title>
      
      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="会員総数"
              value={totalStats.totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="アクティブ会員"
              value={totalStats.activeUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="総ご利用回数"
              value={totalStats.totalOrders}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="総売上"
              value={totalStats.totalRevenue}
              prefix={<DollarOutlined />}
              formatter={(value) => `¥${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* フィルターエリア */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Select
              placeholder="会員状況"
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="active">有効</Option>
              <Option value="inactive">未アクティベート</Option>
              <Option value="banned">利用停止</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="会員ランク"
              style={{ width: '100%' }}
              allowClear
              value={filters.memberLevel}
              onChange={(value) => setFilters(prev => ({ ...prev, memberLevel: value }))}
            >
              <Option value="bronze">ブロンズ会員</Option>
              <Option value="silver">シルバー会員</Option>
              <Option value="gold">ゴールド会員</Option>
              <Option value="platinum">プラチナ会員</Option>
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['登録開始日', '登録終了日']}
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
            />
          </Col>
          <Col span={6}>
            <Input
              placeholder="お名前、電話番号、メールアドレスで検索"
              prefix={<SearchOutlined />}
              value={filters.keyword}
              onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
            />
          </Col>
        </Row>
      </Card>

      {/* 会員リスト */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1000 }}
          pagination={{
            total: filteredUsers.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]}件 / 全${total}件`
          }}
        />
      </Card>

      {/* 会員詳細モーダル */}
      <Modal
        title="会員詳細情報"
        open={detailModalVisible}
        footer={null}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
      >
        {selectedUser && (
          <div>
            <Row gutter={24}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar size={80} src={selectedUser.avatar} icon={<UserOutlined />} />
                  <div style={{ marginTop: 8 }}>
                    <Title level={4}>{selectedUser.name}</Title>
                    <Tag color={memberLevelColors[selectedUser.memberLevel]}>
                      {memberLevelLabels[selectedUser.memberLevel]}
                    </Tag>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <Space direction="vertical" size="small">
                      <Statistic title="総利用回数" value={selectedUser.totalOrders} />
                      <Statistic title="総利用金額" value={selectedUser.totalSpent} formatter={(value) => `¥${Number(value).toLocaleString()}`} />
                      <div>
                        <span style={{ fontWeight: 'bold' }}>評価: </span>
                        <Rate disabled defaultValue={selectedUser.averageRating} style={{ fontSize: 14 }} />
                        <span style={{ marginLeft: 8 }}>{selectedUser.averageRating}</span>
                      </div>
                    </Space>
                  </div>
                </div>
              </Col>
              <Col span={16}>
                <Descriptions title="基本情報" column={2} size="small">
                  <Descriptions.Item label="電話番号">{selectedUser.phone}</Descriptions.Item>
                  <Descriptions.Item label="メールアドレス">{selectedUser.email}</Descriptions.Item>
                  <Descriptions.Item label="性別">{genderLabels[selectedUser.gender]}</Descriptions.Item>
                  <Descriptions.Item label="年齢">{selectedUser.age ? `${selectedUser.age}歳` : '不明'}</Descriptions.Item>
                  <Descriptions.Item label="状況">
                    <Tag color={statusColors[selectedUser.status]}>
                      {statusLabels[selectedUser.status]}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="登録日">{selectedUser.registerDate}</Descriptions.Item>
                  <Descriptions.Item label="最終ログイン">{selectedUser.lastLoginDate}</Descriptions.Item>
                  <Descriptions.Item label="住所" span={2}>{selectedUser.address || '未入力'}</Descriptions.Item>
                  {selectedUser.emergencyContact && (
                    <>
                      <Descriptions.Item label="緑急連絡先">{selectedUser.emergencyContact}</Descriptions.Item>
                      <Descriptions.Item label="緑急連絡先電話">{selectedUser.emergencyPhone}</Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
              <Title level={4}>最近のご利用履歴</Title>
              <List
                dataSource={selectedUser.recentOrders}
                renderItem={(order) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>{order.id}</span>
                          <Tag color={order.type === 'rental' ? 'blue' : 'green'}>
                            {order.type === 'rental' ? 'レンタカー' : 'タクシー'}
                          </Tag>
                          <Tag color="green">{order.status}</Tag>
                        </Space>
                      }
                      description={
                        <Space>
                          <span>車両: {order.vehicle}</span>
                          <span>料金: ¥{order.amount}</span>
                          <span>利用日: {order.date}</span>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;